import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, FileText, CheckCircle, Plus, X } from 'lucide-react';
import Button from './Button';
import { getFunctionsUrl } from '../utils/functions';
import './ScholarshipApplication.css';

// Helper function to upload files to Firebase Storage
const uploadFilesToFirebaseStorage = async (files: File[], applicationId: string, fieldName: string) => {
  try {
    const uploadPromises = files.map(async (file, index) => {
      const fileName = `${applicationId}/${fieldName}/${file.name}`;
      const functionsUrl = await getFunctionsUrl();
      const response = await fetch(`${functionsUrl}/uploadFile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName,
          fileType: file.type,
          fileSize: file.size
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get upload URL');
      }

      // Upload to the signed URL
      const uploadResponse = await fetch(result.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      return result.downloadUrl;
    });

    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading files to Firebase Storage:', error);
    throw error;
  }
};

// Helper function to submit scholarship application to Firestore
const submitScholarshipApplicationToFirestore = async (applicationData: any) => {
  try {
    const functionsUrl = await getFunctionsUrl();
    const response = await fetch(`${functionsUrl}/submitScholarshipApplication`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(applicationData)
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to submit application');
    }

    return result;
  } catch (error) {
    console.error('Error submitting scholarship application to Firestore:', error);
    throw error;
  }
};

interface FormData {
  // Applicant Info
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  dob: string;
  
  // Guardian Info
  guardians: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    sameAddress: boolean;
    address: string;
    city: string;
    state: string;
    zip: string;
  }[];
  
  // School Info
  schoolName: string;
  gpa: string;
  graduationYear: string;
  academicHonors: string;
  communityService: string;
  hobbies: string;
  major: string;
  volleyballExperience: string;
  
  // Terms
  todayDate: string;
  termsAgreement: boolean;
}

const ScholarshipApplication: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [fileUploads, setFileUploads] = useState<{[key: string]: File[]}>({
    recommendationLetters: [],
    collegeAcceptance: [],
    scholarshipLetter: []
  });
  
  // Initialize form data with empty values
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    dob: '',
    
    guardians: [{
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      sameAddress: false,
      address: '',
      city: '',
      state: '',
      zip: ''
    }],
    
    schoolName: '',
    gpa: '',
    graduationYear: '',
    academicHonors: '',
    communityService: '',
    hobbies: '',
    major: '',
    volleyballExperience: '',
    
    todayDate: '',
    termsAgreement: false
  });
  
  const fileUploadRefs = {
    recommendationLetters: useRef<HTMLDivElement>(null),
    collegeAcceptance: useRef<HTMLDivElement>(null),
    scholarshipLetter: useRef<HTMLDivElement>(null)
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  // Handle checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: checked
    }));
  };
  
  // Handle guardian input changes
  const handleGuardianChange = (index: number, field: string, value: string | boolean) => {
    setFormData(prevData => {
      const updatedGuardians = [...prevData.guardians];
      updatedGuardians[index] = {
        ...updatedGuardians[index],
        [field]: value
      };
      
      return {
        ...prevData,
        guardians: updatedGuardians
      };
    });
  };

  // Handle "Same as Applicant" checkbox
  useEffect(() => {
    formData.guardians.forEach((guardian, index) => {
      if (guardian.sameAddress) {
        setFormData(prevData => {
          const updatedGuardians = [...prevData.guardians];
          updatedGuardians[index] = {
            ...updatedGuardians[index],
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip
          };
          
          return {
            ...prevData,
            guardians: updatedGuardians
          };
        });
      }
    });
  }, [formData.guardians.map(g => g.sameAddress).join(','), formData.address, formData.city, formData.state, formData.zip]);

  // Handle file uploads
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.classList.add('drag-over');
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.classList.remove('drag-over');
      }
    };

    const handleDrop = (e: DragEvent, fieldName: string) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.classList.remove('drag-over');
        
        if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
          const files = Array.from(e.dataTransfer.files);
          handleFileChange(files, fieldName);
        }
      }
    };

    // Add event listeners for each file upload area
    Object.keys(fileUploadRefs).forEach(key => {
      const ref = fileUploadRefs[key as keyof typeof fileUploadRefs].current;
      if (ref) {
        ref.addEventListener('dragover', handleDragOver, { passive: true });
        ref.addEventListener('dragleave', handleDragLeave, { passive: true });
        ref.addEventListener('drop', (e) => handleDrop(e, key), { passive: true });
      }
    });

    // Cleanup
    return () => {
      Object.keys(fileUploadRefs).forEach(key => {
        const ref = fileUploadRefs[key as keyof typeof fileUploadRefs].current;
        if (ref) {
          ref.removeEventListener('dragover', handleDragOver);
          ref.removeEventListener('dragleave', handleDragLeave);
          ref.removeEventListener('drop', (e) => handleDrop(e, key));
        }
      });
    };
  }, [currentStep]);

  const handleFileChange = (files: File[], fieldName: string) => {
    // Update state with selected files
    setFileUploads(prev => ({
      ...prev,
      [fieldName]: fieldName === 'recommendationLetters' ? [...prev[fieldName], ...files] : files
    }));

    // Update the UI to show selected files
    const uploadArea = fileUploadRefs[fieldName as keyof typeof fileUploadRefs].current;
    if (uploadArea) {
      const contentDiv = uploadArea.querySelector('.file-upload-content');
      if (contentDiv) {
        const fileList = document.createElement('div');
        fileList.className = 'file-list';
        
        files.forEach(file => {
          const fileItem = document.createElement('div');
          fileItem.className = 'file-item';
          fileItem.textContent = file.name;
          fileList.appendChild(fileItem);
        });
        
        // Replace content with file list
        contentDiv.innerHTML = '';
        contentDiv.appendChild(fileList);
      }
    }
  };

  const handleAddGuardian = () => {
    setFormData(prevData => ({
      ...prevData,
      guardians: [
        ...prevData.guardians,
        {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          sameAddress: false,
          address: '',
          city: '',
          state: '',
          zip: ''
        }
      ]
    }));
  };
  
  const handleRemoveGuardian = (index: number) => {
    if (formData.guardians.length > 1) {
      setFormData(prevData => ({
        ...prevData,
        guardians: prevData.guardians.filter((_, i) => i !== index)
      }));
    }
  };

  const nextStep = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Submit the application to Firestore first to get an application ID
      const result = await submitScholarshipApplicationToFirestore({
        type,
        ...formData,
        fileUploads: Object.keys(fileUploads).reduce((acc, key) => {
          acc[key] = fileUploads[key].map(file => ({
            name: file.name,
            size: file.size,
            type: file.type
          }));
          return acc;
        }, {} as any)
      });

      // Upload files to Firebase Storage if we have an application ID
      if (result.applicationId) {
        try {
          const uploadResults = await Promise.all(
            Object.entries(fileUploads).map(async ([fieldName, files]) => {
              if (files.length > 0) {
                try {
                  const downloadUrls = await uploadFilesToFirebaseStorage(files, result.applicationId, fieldName);
                  return { fieldName, downloadUrls };
                } catch (error) {
                  console.error(`Error uploading ${fieldName} files:`, error);
                  // Continue with other files even if one fails
                  return { fieldName, downloadUrls: [] };
                }
              }
              return { fieldName, downloadUrls: [] };
            })
          );

          // Update Firestore with the download URLs (only if we have any successful uploads)
          const fileUrls = uploadResults.reduce((acc, result) => {
            if (result.downloadUrls.length > 0) {
              acc[result.fieldName] = result.downloadUrls;
            }
            return acc;
          }, {} as any);

          if (Object.keys(fileUrls).length > 0) {
            try {
              const functionsUrl = await getFunctionsUrl();
        await fetch(`${functionsUrl}/updateApplicationFiles`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  applicationId: result.applicationId,
                  fileUrls
                })
              });
            } catch (updateError) {
              console.error('Error updating application files:', updateError);
              // Continue even if file URL update fails
            }
          }
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          // Continue with form submission even if file uploads fail
        }
      }
      
      // Submit the form
      setFormSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('There was an error submitting your application. Please try again.');
    }
  };

  const scholarshipTitle = type === 'athletic' ? 'Athletic Scholarship' : 'Academic Scholarship';

  if (formSubmitted) {
    return (
      <div className="application-page">
        <div className="application-success">
          <div className="container">
            <CheckCircle size={64} />
            <h1>Application Submitted Successfully!</h1>
            <p>Thank you for applying to the Carly Faye Foundation {scholarshipTitle}. We will review your application and be in touch soon.</p>
            <div className="success-actions">
              <Button 
                href="/scholarships" 
                variant="secondary"
                icon={ArrowRight}
              >
                Return to Scholarships
              </Button>
              <Button 
                href="/" 
                variant="primary"
                icon={ArrowRight}
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="application-page">
      <Helmet>
        <title>{scholarshipTitle} Application – Carly Faye Foundation</title>
        <meta name="description" content={`Apply for the Carly Faye Foundation ${scholarshipTitle}. Submit your information and required documents online.`} />
        <link rel="canonical" href={`https://carly-faye-foundation.web.app/scholarships/apply/${type === 'athletic' ? 'athletic' : 'academic'}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${scholarshipTitle} Application – Carly Faye Foundation`} />
        <meta property="og:description" content={`Apply for the ${scholarshipTitle} supporting young women in their pursuit of excellence.`} />
        <meta property="og:url" content={`https://carly-faye-foundation.web.app/scholarships/apply/${type === 'athletic' ? 'athletic' : 'academic'}`} />
        <meta property="og:image" content="/images/cf-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${scholarshipTitle} Application – Carly Faye Foundation`} />
        <meta name="twitter:description" content={`Apply for the ${scholarshipTitle} supporting young women in their pursuit of excellence.`} />
        <meta name="twitter:image" content="/images/cf-logo.png" />
      </Helmet>
      <div className="page-header">
        <div className="container">
          <h1>{scholarshipTitle} Application</h1>
        </div>
      </div>

      <div className="application-container">
        <div className="container">
          <div className="application-progress">
            <div className="progress-steps">
              <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-label">Applicant Info</div>
              </div>
              <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-label">Guardian Info</div>
              </div>
              <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-label">School Info</div>
              </div>
              <div className={`progress-step ${currentStep >= 4 ? 'active' : ''}`}>
                <div className="step-number">4</div>
                <div className="step-label">Documents</div>
              </div>
              <div className={`progress-step ${currentStep >= 5 ? 'active' : ''}`}>
                <div className="step-number">5</div>
                <div className="step-label">Review & Submit</div>
              </div>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${(currentStep / 5) * 100}%` }}></div>
            </div>
          </div>

          <div className="application-form">
            <form onSubmit={currentStep === 5 ? handleFormSubmit : nextStep}>
              {currentStep === 1 && (
                <div className="form-step">
                  <h2>Applicant Information</h2>
                  <p className="step-description">Please provide your personal information.</p>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input 
                        type="text" 
                        id="firstName" 
                        name="firstName" 
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input 
                        type="text" 
                        id="lastName" 
                        name="lastName" 
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="address">Address *</label>
                    <input 
                      type="text" 
                      id="address" 
                      name="address" 
                      value={formData.address}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input 
                        type="text" 
                        id="city" 
                        name="city" 
                        value={formData.city}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="state">State *</label>
                      <input 
                        type="text" 
                        id="state" 
                        name="state" 
                        value={formData.state}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="zip">ZIP Code *</label>
                      <input 
                        type="text" 
                        id="zip" 
                        name="zip" 
                        value={formData.zip}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone *</label>
                      <input 
                        type="tel" 
                        id="phone" 
                        name="phone" 
                        value={formData.phone}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email" 
                        value={formData.email}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="dob">Date of Birth *</label>
                      <input 
                        type="date" 
                        id="dob" 
                        name="dob" 
                        value={formData.dob}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="form-step">
                  <h2>Guardian Information</h2>
                  <p className="step-description">Please provide information about your parent or guardian.</p>
                  
                  {formData.guardians.map((guardian, index) => (
                    <div key={index} className="guardian-section">
                      {index > 0 && (
                        <div className="guardian-header">
                          <h3>Guardian {index + 1}</h3>
                          <button 
                            type="button" 
                            className="remove-guardian-btn"
                            onClick={() => handleRemoveGuardian(index)}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor={`guardian-${index}-firstName`}>First Name *</label>
                          <input 
                            type="text" 
                            id={`guardian-${index}-firstName`}
                            name={`guardian-${index}-firstName`}
                            value={guardian.firstName}
                            onChange={(e) => handleGuardianChange(index, 'firstName', e.target.value)}
                            required 
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor={`guardian-${index}-lastName`}>Last Name *</label>
                          <input 
                            type="text" 
                            id={`guardian-${index}-lastName`}
                            name={`guardian-${index}-lastName`}
                            value={guardian.lastName}
                            onChange={(e) => handleGuardianChange(index, 'lastName', e.target.value)}
                            required 
                          />
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor={`guardian-${index}-email`}>Email *</label>
                          <input 
                            type="email" 
                            id={`guardian-${index}-email`}
                            name={`guardian-${index}-email`}
                            value={guardian.email}
                            onChange={(e) => handleGuardianChange(index, 'email', e.target.value)}
                            required 
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor={`guardian-${index}-phone`}>Phone *</label>
                          <input 
                            type="tel" 
                            id={`guardian-${index}-phone`}
                            name={`guardian-${index}-phone`}
                            value={guardian.phone}
                            onChange={(e) => handleGuardianChange(index, 'phone', e.target.value)}
                            required 
                          />
                        </div>
                      </div>
                      
                      <div className="form-group checkbox-group">
                        <input 
                          type="checkbox" 
                          id={`guardian-${index}-sameAddress`}
                          name={`guardian-${index}-sameAddress`}
                          checked={guardian.sameAddress}
                          onChange={(e) => handleGuardianChange(index, 'sameAddress', e.target.checked)}
                        />
                        <label htmlFor={`guardian-${index}-sameAddress`}>Same address as applicant</label>
                      </div>
                      
                      {!guardian.sameAddress && (
                        <div className="guardian-address-fields">
                          <div className="form-group">
                            <label htmlFor={`guardian-${index}-address`}>Address *</label>
                            <input 
                              type="text" 
                              id={`guardian-${index}-address`}
                              name={`guardian-${index}-address`}
                              value={guardian.address}
                              onChange={(e) => handleGuardianChange(index, 'address', e.target.value)}
                              required={!guardian.sameAddress}
                            />
                          </div>
                          
                          <div className="form-row">
                            <div className="form-group">
                              <label htmlFor={`guardian-${index}-city`}>City *</label>
                              <input 
                                type="text" 
                                id={`guardian-${index}-city`}
                                name={`guardian-${index}-city`}
                                value={guardian.city}
                                onChange={(e) => handleGuardianChange(index, 'city', e.target.value)}
                                required={!guardian.sameAddress}
                              />
                            </div>
                            
                            <div className="form-group">
                              <label htmlFor={`guardian-${index}-state`}>State *</label>
                              <input 
                                type="text" 
                                id={`guardian-${index}-state`}
                                name={`guardian-${index}-state`}
                                value={guardian.state}
                                onChange={(e) => handleGuardianChange(index, 'state', e.target.value)}
                                required={!guardian.sameAddress}
                              />
                            </div>
                            
                            <div className="form-group">
                              <label htmlFor={`guardian-${index}-zip`}>ZIP Code *</label>
                              <input 
                                type="text" 
                                id={`guardian-${index}-zip`}
                                name={`guardian-${index}-zip`}
                                value={guardian.zip}
                                onChange={(e) => handleGuardianChange(index, 'zip', e.target.value)}
                                required={!guardian.sameAddress}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <button 
                    type="button" 
                    className="secondary-button add-guardian-btn"
                    onClick={handleAddGuardian}
                  >
                    <Plus size={16} /> Add Additional Guardian
                  </button>
                </div>
              )}

              {currentStep === 3 && (
                <div className="form-step">
                  <h2>School Information</h2>
                  <p className="step-description">Please provide information about your education and activities.</p>
                  
                  <div className="form-group">
                    <label htmlFor="schoolName">Name of School *</label>
                    <input 
                      type="text" 
                      id="schoolName" 
                      name="schoolName" 
                      value={formData.schoolName}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="gpa">Current GPA *</label>
                      <input 
                        type="number" 
                        id="gpa" 
                        name="gpa" 
                        min="0" 
                        max="4.0" 
                        step="0.01" 
                        value={formData.gpa}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="graduationYear">Graduation Year *</label>
                      <select 
                        id="graduationYear" 
                        name="graduationYear" 
                        value={formData.graduationYear}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Year</option>
                        <option value="2025">2025</option>
                        <option value="2026">2026</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="academicHonors">Academic Honors and Achievements</label>
                    <textarea 
                      id="academicHonors" 
                      name="academicHonors" 
                      rows={4} 
                      placeholder="List any academic honors, awards, or achievements"
                      value={formData.academicHonors}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                  
                  {type === 'athletic' && (
                    <div className="form-group">
                      <label htmlFor="volleyballExperience">Volleyball Experience and Honors *</label>
                      <textarea 
                        id="volleyballExperience" 
                        name="volleyballExperience" 
                        rows={4} 
                        placeholder="Describe your volleyball experience, positions played, teams, tournaments, and any honors or awards received"
                        value={formData.volleyballExperience}
                        onChange={handleInputChange}
                        required
                      ></textarea>
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label htmlFor="communityService">Community Service</label>
                    <textarea 
                      id="communityService" 
                      name="communityService" 
                      rows={4} 
                      placeholder="Describe your community service activities"
                      value={formData.communityService}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="hobbies">Hobbies and Outside Interests</label>
                    <textarea 
                      id="hobbies" 
                      name="hobbies" 
                      rows={4} 
                      placeholder="Describe your hobbies and outside interests"
                      value={formData.hobbies}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="major">What major or specialty will you pursue? *</label>
                    <input 
                      type="text" 
                      id="major" 
                      name="major" 
                      value={formData.major}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="form-step">
                  <h2>Required Documents</h2>
                  <p className="step-description">Please upload all required documents for your application.</p>
                  
                  <div className="form-group file-upload">
                    <label htmlFor="recommendationLetters">Letters of Recommendation</label>
                    <div 
                      className="file-upload-area" 
                      id="recommendationLetters-upload"
                      ref={fileUploadRefs.recommendationLetters}
                    >
                      <input 
                        type="file" 
                        id="recommendationLetters" 
                        name="recommendationLetters" 
                        multiple 
                        accept=".pdf,.doc,.docx" 
                        className="file-input"
                        onChange={(e) => e.target.files && handleFileChange(Array.from(e.target.files), 'recommendationLetters')}
                      />
                      <div className="file-upload-content">
                        <div className="file-upload-icon">
                          <FileText size={32} />
                        </div>
                        <p>Drag and drop files here or <span>click to upload</span></p>
                        <p className="file-upload-info">PDF or Word documents only (max 5MB)</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group file-upload">
                    <label htmlFor="collegeAcceptance">College Acceptance or Enrollment Verification</label>
                    <div 
                      className="file-upload-area" 
                      id="collegeAcceptance-upload"
                      ref={fileUploadRefs.collegeAcceptance}
                    >
                      <input 
                        type="file" 
                        id="collegeAcceptance" 
                        name="collegeAcceptance" 
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" 
                        className="file-input"
                        onChange={(e) => e.target.files && handleFileChange(Array.from(e.target.files), 'collegeAcceptance')}
                      />
                      <div className="file-upload-content">
                        <div className="file-upload-icon">
                          <FileText size={32} />
                        </div>
                        <p>Drag and drop file here or <span>click to upload</span></p>
                        <p className="file-upload-info">PDF, Word, or image files (max 5MB)</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group file-upload">
                    <label htmlFor="scholarshipLetter">
                      {type === 'academic' ? 'Personal Essay' : 'Letter to the Scholarship Committee'}
                    </label>
                    <div 
                      className="file-upload-area" 
                      id="scholarshipLetter-upload"
                      ref={fileUploadRefs.scholarshipLetter}
                    >
                      <input 
                        type="file" 
                        id="scholarshipLetter" 
                        name="scholarshipLetter" 
                        accept=".pdf,.doc,.docx" 
                        className="file-input"
                        onChange={(e) => e.target.files && handleFileChange(Array.from(e.target.files), 'scholarshipLetter')}
                      />
                      <div className="file-upload-content">
                        <div className="file-upload-icon">
                          <FileText size={32} />
                        </div>
                        <p>Drag and drop file here or <span>click to upload</span></p>
                        <p className="file-upload-info">PDF or Word documents only (max 5MB)</p>
                      </div>
                    </div>
                    {type === 'academic' ? (
                      <small>
                        <strong>Essay Requirements:</strong> Please answer one of the following questions:
                        <br />• What is your biggest dream? And what is the first step or steps you have taken or plan to take to fulfill your dream?
                        <br />• What is the biggest adversity you have overcome and what did it teach you?
                        <br />This essay must be at least 1-2 pages, typed, double-spaced, and 12 pt.
                      </small>
                    ) : (
                      <small>Please upload a 1-page letter explaining yourself, your volleyball experience, your future goals, and why you are deserving of this scholarship.</small>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 5 && (
                <div className="form-step">
                  <h2>Review & Submit</h2>
                  <p className="step-description">Please review your application and agree to the terms before submitting.</p>
                  
                  <div className="application-summary">
                    <h3>Application Summary</h3>
                    <p>Please review all information before submitting. You can go back to previous steps to make changes if needed.</p>
                    
                    <div className="summary-section">
                      <h4>Applicant Information</h4>
                      <div className="summary-content">
                        <div className="summary-row">
                          <span className="summary-label">Name:</span>
                          <span className="summary-value">{formData.firstName} {formData.lastName}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Address:</span>
                          <span className="summary-value">{formData.address}, {formData.city}, {formData.state} {formData.zip}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Phone:</span>
                          <span className="summary-value">{formData.phone}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Email:</span>
                          <span className="summary-value">{formData.email}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Date of Birth:</span>
                          <span className="summary-value">{formData.dob}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="summary-section">
                      <h4>Guardian Information</h4>
                      <div className="summary-content">
                        {formData.guardians.map((guardian, index) => (
                          <div key={index} className="guardian-summary">
                            {formData.guardians.length > 1 && <h5>Guardian {index + 1}</h5>}
                            <div className="summary-row">
                              <span className="summary-label">Name:</span>
                              <span className="summary-value">{guardian.firstName} {guardian.lastName}</span>
                            </div>
                            <div className="summary-row">
                              <span className="summary-label">Email:</span>
                              <span className="summary-value">{guardian.email}</span>
                            </div>
                            <div className="summary-row">
                              <span className="summary-label">Phone:</span>
                              <span className="summary-value">{guardian.phone}</span>
                            </div>
                            <div className="summary-row">
                              <span className="summary-label">Address:</span>
                              <span className="summary-value">
                                {guardian.sameAddress 
                                  ? `Same as applicant` 
                                  : `${guardian.address}, ${guardian.city}, ${guardian.state} ${guardian.zip}`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="summary-section">
                      <h4>School Information</h4>
                      <div className="summary-content">
                        <div className="summary-row">
                          <span className="summary-label">School:</span>
                          <span className="summary-value">{formData.schoolName}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">GPA:</span>
                          <span className="summary-value">{formData.gpa}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Graduation Year:</span>
                          <span className="summary-value">{formData.graduationYear}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Intended Major:</span>
                          <span className="summary-value">{formData.major}</span>
                        </div>
                        {formData.academicHonors && (
                          <div className="summary-row">
                            <span className="summary-label">Academic Honors:</span>
                            <span className="summary-value">{formData.academicHonors}</span>
                          </div>
                        )}
                        {formData.communityService && (
                          <div className="summary-row">
                            <span className="summary-label">Community Service:</span>
                            <span className="summary-value">{formData.communityService}</span>
                          </div>
                        )}
                        {formData.hobbies && (
                          <div className="summary-row">
                            <span className="summary-label">Hobbies:</span>
                            <span className="summary-value">{formData.hobbies}</span>
                          </div>
                        )}
                        {type === 'athletic' && formData.volleyballExperience && (
                          <div className="summary-row">
                            <span className="summary-label">Volleyball Experience:</span>
                            <span className="summary-value">{formData.volleyballExperience}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="summary-section">
                      <h4>Documents</h4>
                      <div className="summary-content">
                        <div className="summary-row">
                          <span className="summary-label">Recommendation Letters:</span>
                          <span className="summary-value">
                            {fileUploads.recommendationLetters.length > 0 
                              ? fileUploads.recommendationLetters.map(file => file.name).join(', ')
                              : 'No files uploaded'}
                          </span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">College Acceptance:</span>
                          <span className="summary-value">
                            {fileUploads.collegeAcceptance.length > 0 
                              ? fileUploads.collegeAcceptance.map(file => file.name).join(', ')
                              : 'No files uploaded'}
                          </span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Scholarship Letter:</span>
                          <span className="summary-value">
                            {fileUploads.scholarshipLetter.length > 0 
                              ? fileUploads.scholarshipLetter.map(file => file.name).join(', ')
                              : 'No files uploaded'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="todayDate">Today's Date *</label>
                    <input 
                      type="date" 
                      id="todayDate" 
                      name="todayDate" 
                      value={formData.todayDate}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  
                  <div className="form-group checkbox-group terms-checkbox">
                    <input 
                      type="checkbox" 
                      id="termsAgreement" 
                      name="termsAgreement" 
                      checked={formData.termsAgreement}
                      onChange={handleCheckboxChange}
                      required 
                    />
                    <label htmlFor="termsAgreement">
                      I hereby affirm that all the above stated information provided by me is true and correct to the best of my knowledge. 
                      I also consent that my picture may be taken and used for any purpose deemed necessary to promote the Foundation's 
                      scholarship program. I hereby understand that if chosen as a scholarship recipient, according to Carly Faye Foundation 
                      Scholarship policy, I must provide evidence of enrollment/registration at the post-secondary institution of my choice 
                      before scholarship funds can be awarded.
                    </label>
                  </div>
                </div>
              )}

              <div className="form-navigation">
                {currentStep > 1 && (
                  <Button 
                    variant="secondary"
                    icon={ArrowLeft}
                    iconPosition="left"
                    onClick={prevStep}
                  >
                    Previous
                  </Button>
                )}
                
                <Button 
                  variant="primary"
                  icon={ArrowRight}
                  type="submit"
                >
                  {currentStep < 5 ? 'Next' : 'Submit Application'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScholarshipApplication; 